import { useParams } from "react-router";
import { create } from "zustand";
import { useEffect } from "react";
import { useMemo } from 'react';

import useMenuQrCode from "../../hooks/QrCode/useMenuQrCode";

import Spinner from "../../components/ui/spinner";

import MenuItemQrCodeGrid from "../../components/common/QrCode/MenuItemQrCodeGrid";

import QrCodeHeader from "./QrCodeHeader";

export const useCurrentLang = create((set) => ({
  currentLang: '',

  setCurrentLang: (newLang: string) =>
    set(() => ({
      currentLang: newLang,
    })),
}));

const QrCodePage = () => {
  const { public_id: publicId } = useParams();

  const { menu, loading } = useMenuQrCode(publicId, {
    fetchPolicy: "network-only",
    refetchOnMount: false,
  });

  const setCurrentLang = useCurrentLang(
    (state: unknown) =>
      (
        state as {
          setCurrentLang: (newLang: string) => void;
        }
      ).setCurrentLang
  );

  const sectionsWithItems = useMemo(() => {
    if (!menu || !menu.menuSections || !menu.menuItems) {
      return [];
    }
    if (!menu.menuSections.length) {
      return [{
        section: null,
        items: menu.menuItems
      }];
    }

    return menu.menuSections.flatMap((section) => {
      const items = menu.menuItems?.filter((item) =>
        item.menuSectionIds?.includes(section.id.toString())
      );

      if (!items || items.length === 0) {
        return [];
      }

      return {
        section,
        items,
      };
    });
  }, [menu]);

  useEffect(() => {
    if (menu?.mainLanguage) {
      setCurrentLang(menu.mainLanguage);
    }
  }, [menu?.mainLanguage, setCurrentLang]);

  if (loading) {
    return (
      <div
        className="
          min-h-screen
          flex items-center justify-center

          bg-white
          dark:bg-gray-950
        "
      >
        <Spinner />
      </div>
    );
  }

  if (!menu) {
    return (
      <div
        className="
          min-h-screen
          flex items-center justify-center

          bg-white
          dark:bg-gray-950

          px-5
        "
      >
        <div
          className="
            rounded-3xl
            border border-gray-200
            bg-white
            px-8 py-10

            dark:border-gray-800
            dark:bg-gray-900

            shadow-xl
            text-center
          "
        >
          <h3
            className="
              text-2xl font-bold
              text-gray-900
              dark:text-white
            "
          >
            Menu not found
          </h3>

          <p
            className="
              mt-2
              text-gray-500
              dark:text-gray-400
            "
          >
            This QR menu does not exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="
        min-h-screen
        bg-white
        dark:bg-gray-950
      "
    >
      <QrCodeHeader
        sectionsWithItems={sectionsWithItems}
        menu={menu}
      />

      <MenuItemQrCodeGrid
        sectionsWithItems={sectionsWithItems}
        menu={menu}
      />
    </div>
  );
};

export default QrCodePage;
